import { formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { EnvironmentService } from 'src/app/shared/common/environment/environment.service';
import { MapWorkspace } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { RelationshipService } from 'src/app/shared/map-data-services/relationship/relationship.service';
import { Actor } from 'src/app/shared/user/actor';
import { User, UserDTO } from 'src/app/shared/user/user';

import { PermissionType } from '../../map-data-services/mapWorkspace/permission';
import { UserService } from '../../user/user.service';
import { TCObjectType } from '../files/TCFile.service';
import {
    LegacyGeoDataUpdateJob,
    LegacyGeoDataUpdateJobs,
    LegacyTask,
    LegacyTaskDTOStatus,
    LegacyTaskStatus,
    LegacyToDoDTO
} from './legacy-task';

@Injectable({
    providedIn: 'root'
})
export class LegacyTaskService {
    constructor(
        private http: HttpClient,
        private env: EnvironmentService,
        private relationshipService: RelationshipService,
        private userService: UserService
    ) {}

    async getTasksBySearch(
        projectId: string,
        workspaceId: string,
        status: LegacyTaskDTOStatus,
        searchInput: string = ''
    ): Promise<LegacyTask[]> {
        let taskPath =
            this.env.apiUrl +
            '/projects/' +
            projectId +
            '/dataupdatejobs/_search?workspaceId=' +
            workspaceId +
            '&status=' +
            status +
            '&pageSize=100';
        if (searchInput) {
            taskPath += '&title=' + searchInput;
        }
        const taskResponse = await lastValueFrom(this.http.get<LegacyGeoDataUpdateJobs>(taskPath));
        return taskResponse.items.map(item => LegacyTask.fromDTOandTodo(item, null));
    }

    async getTaskById(projectId: string, id: string): Promise<LegacyTask> {
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/dataupdatejobs' + '/' + id;

        const taskResponse = await lastValueFrom(this.http.get<LegacyGeoDataUpdateJob>(taskPath));
        const todoPath = this.env.connectApiUrl + '/todos' + '/' + taskResponse.todoId;
        const todoResponse = await lastValueFrom(
            this.http.get<LegacyToDoDTO>(todoPath, { params: { projectId: projectId } })
        );
        let tmpTask = LegacyTask.fromDTOandTodo(taskResponse, todoResponse);
        return await this.checkTodoHasReturnJobUsers(projectId, tmpTask);
    }

    async checkTodoHasReturnJobUsers(projectId: string, tmpTask: LegacyTask): Promise<LegacyTask> {
        if (tmpTask.isTodoCompletedByUnassignedUser) {
            let promises: Promise<Partial<UserDTO>>[] = [];
            tmpTask.returnedJobs.forEach(async job => {
                promises.push(
                    this.userService.getProjectUser(projectId, job.userId).then(user => user) as Promise<
                        Partial<UserDTO>
                    >
                );
            });
            const responses = await Promise.all(promises);
            responses.forEach(response => {
                const tmpActor = Actor.fromDTO(response, PermissionType.USER);
                tmpActor.isTodoCompleted = true;
                tmpTask.unassignedCompletedUser.push(tmpActor);
            });
            return tmpTask;
        }
        return Promise.resolve(tmpTask);
    }

    async createOrUpdateTask(projectId: string, workspace: MapWorkspace, task: LegacyTask): Promise<LegacyTask> {
        let todoPath = this.env.connectApiUrl + '/todos';
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/dataupdatejobs';
        let dueDate = task.dueDate ? formatDate(task.dueDate, 'yyyy-MM-ddT23:59:59Z', 'en-US', null) : null;

        // Convert any USER/GROUP to USER_LEGACY/GROUP_LEGACY in assignees before sending to API
        if (task.assignees != null) {
            task.assignees.forEach(assignee => {
                const actor = assignee as Actor;
                if (assignee.type === PermissionType.USER) {
                    assignee.type = PermissionType.USER_LEGACY;
                    actor.actorId = actor.actorId.replace(PermissionType.USER, PermissionType.USER_LEGACY);
                } else if (assignee.type === PermissionType.GROUP) {
                    assignee.type = PermissionType.GROUP_LEGACY;
                    actor.actorId = actor.actorId.replace(PermissionType.GROUP, PermissionType.GROUP_LEGACY);
                }
            });
        }

        let todoObject = {
            description: task.description,
            projectId: projectId,
            priority: task.priority,
            status: LegacyTaskStatus.IN_PROGRESS,
            assignees: task.assignees,
            dueDate: dueDate,
            title: task.title,
            type: task.type ? task.type : 'Request'
        };
        let taskResponse: LegacyGeoDataUpdateJob;
        let todoResponse: LegacyToDoDTO;
        if (task.id == null) {
            todoResponse = await lastValueFrom(this.http.post<LegacyToDoDTO>(todoPath, todoObject));
            let todoId = todoResponse.id;
            let todoAttachmentsPath = this.env.connectApiUrl + '/todos/' + todoId + '/attachments';
            await lastValueFrom(
                this.http.post(todoAttachmentsPath, [
                    {
                        id: workspace.connectFileId,
                        type: TCObjectType.FILE
                    }
                ])
            );
            let taskData = {
                todoId,
                forms: task.features.map(taskFeature => ({
                    formId: taskFeature.featureId,
                    templateSeriesId: taskFeature.templateSeriesId,
                    status: taskFeature.status
                }))
            };
            taskResponse = await lastValueFrom(this.http.post<LegacyGeoDataUpdateJob>(taskPath, taskData));
            let relationshipProperties = {
                dataUpdateJobIds: [taskResponse.id]
            };
            await this.relationshipService.attachToMapWorkspace(projectId, workspace.id, relationshipProperties);
        } else {
            Object.assign(todoObject, { id: task.todoId });
            let taskData = task.toDTO();
            todoResponse = await lastValueFrom(
                this.http.patch<LegacyToDoDTO>(todoPath + '/' + task.todoId, todoObject)
            );
            taskResponse = await lastValueFrom(
                this.http.put<LegacyGeoDataUpdateJob>(taskPath + '/' + task.id, taskData)
            );
        }
        return LegacyTask.fromDTOandTodo(taskResponse, todoResponse);
    }

    closeTask(projectId: string, user: User, task: LegacyTask): Promise<LegacyTask> {
        task.isClosed = true;
        let todoPath = this.env.connectApiUrl + '/todos';
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/dataupdatejobs';
        let taskData = task.toDTO();

        return lastValueFrom(this.http.put<LegacyGeoDataUpdateJob>(taskPath + '/' + task.id, taskData)).then(
            async taskResponse => {
                const todoResponse = await lastValueFrom(
                    this.http.patch<LegacyToDoDTO>(todoPath + '/' + task.todoId, {
                        status: LegacyTaskStatus.CLOSED
                    })
                );
                return LegacyTask.fromDTOandTodo(taskResponse, todoResponse);
            },
            error => Promise.reject(error)
        );
    }

    reopenTask(projectId: string, task: LegacyTask): Promise<LegacyTask> {
        task.isClosed = false;
        let todoPath = this.env.connectApiUrl + '/todos';
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/dataupdatejobs';

        return lastValueFrom(this.http.delete(taskPath + '/' + task.id + '/returns')).then(
            async () => {
                const taskResponse = await lastValueFrom(
                    this.http.get<LegacyGeoDataUpdateJob>(taskPath + '/' + task.id)
                );
                const todoResponse = await lastValueFrom(
                    this.http.patch<LegacyToDoDTO>(todoPath + '/' + task.todoId, {
                        status: LegacyTaskStatus.IN_PROGRESS
                    })
                );
                return LegacyTask.fromDTOandTodo(taskResponse, todoResponse);
            },
            error => Promise.reject(error)
        );
    }

    async deleteTask(projectId: string, task: LegacyTask): Promise<LegacyGeoDataUpdateJob> {
        task.isClosed = true;
        let todoPath = this.env.connectApiUrl + '/todos';
        let taskPath = this.env.apiUrl + '/projects/' + projectId + '/dataupdatejobs';
        const todoAndTask: [LegacyToDoDTO, LegacyGeoDataUpdateJob] = await Promise.all([
            lastValueFrom(this.http.delete<LegacyToDoDTO>(todoPath + '/' + task.todoId)),
            lastValueFrom(this.http.delete<LegacyGeoDataUpdateJob>(taskPath + '/' + task.id))
        ]);
        return todoAndTask[1];
    }
}
